import { Injectable, OnDestroy } from "@angular/core";
import { LTaskType } from "core/dto";
import { IDisposable } from "core/dynamic-form";
import { PdfViewerService } from "core/pdf-new";
import { BehaviorSubject, Subject, take, takeUntil } from "rxjs";
import { IActivity } from "../../dto-map/i-activity";
import { ITask } from "../../dto-map/i-task";
import { RemoteTaskService } from "../../remotes/remote-task.service";
import { SignProcessWizardService } from "../../sign-process-wizard.service";

export interface ISignaturePosition {
  height: number, width: number, top: number, left: number, activityId: string
}

@Injectable()
export class TaskCollectionService implements OnDestroy {
  private readonly _destroy = new Subject<void>();
  private readonly _disposables: IDisposable[] = []
  readonly tasksSubject = this._signProcessWizardService.tasksSubject;

  readonly signaturePositionsSubject = new BehaviorSubject<{ [signatureId: string]: ISignaturePosition }>({});
  readonly scrollTopSubject = this._viewerService.scrollTopSubject;
  get scrollTop() {
    return this.scrollTopSubject.value;
  }

  readonly splugParametersConfigurationsSubject = this._signProcessWizardService.splugParametersConfigurationsSubject;
  get splugParametersConfigurations() {
    return this.splugParametersConfigurationsSubject.value;
  }

  get tasks() {
    return this.tasksSubject.value
  }

  get signatures() {
    if (!this.tasks) {
      return null;
    }
    return this.tasks.filter(task => task.type === 'SIGNATURE')
  }
  constructor(
    private readonly _signProcessWizardService: SignProcessWizardService,
    private readonly _remoteTaskService: RemoteTaskService,
    private readonly _viewerService: PdfViewerService

  ) {


    this._remoteTaskService.localCollectionStore.stateSubject
      .pipe(takeUntil(this._destroy))
      .subscribe((tasks => this.tasksSubject.next(tasks as any)))
  }

  addTask<TTaskType extends LTaskType>(task: ITask<TTaskType>) {

    this._remoteTaskService.insert(task, 'reloadChangedRemote').pipe(take(1)).subscribe();
  }


  removeTask(id: string) {

    return this._remoteTaskService.remove(id, 'reloadChanged')

  }

  updateTask(id: string, task: ITask) {
    this._remoteTaskService.update(id, task, 'reloadChanged').pipe(take(1)).subscribe();;
  }

  getActivityConfiguredSignatureTypes(activty: IActivity) {
    const configuredSignatureTypes = this.splugParametersConfigurations
      .filter(({ actorId }) => activty.actor.id === actorId)
      .map(({ sigTypeId }) => sigTypeId)

    return configuredSignatureTypes;
  }

  nextSignature(activityId: string) {

    const nextPosition = Object.values(this.signaturePositionsSubject.value)
      .filter((position) => activityId === position.activityId && position.top > this.scrollTop)
      .reduce<ISignaturePosition>((min, position) => {
        if (min === undefined || position.top < min.top) {
          min = position
        }
        return min;
      },
        undefined
      )


    if (!nextPosition) {
      return;
    }

    this.scrollTopSubject.next(Math.ceil(nextPosition.top));

  }

  prevSignature(activityId: string) {

    const prevPosition = Object.values(this.signaturePositionsSubject.value)
      .filter((position) => activityId === position.activityId && position.top < this.scrollTop)
      .reduce<ISignaturePosition>((max, position) => {
        if (max === undefined || position.top > max.top) {
          max = position
        }
        return max;
      },
        undefined
      )


    if (!prevPosition) {
      return;
    }

    this.scrollTopSubject.next(Math.ceil(prevPosition.top));
  }


  ngOnDestroy() {
    this._disposables.forEach(d => d.dispose());
    this._destroy.next()
    this._destroy.complete()
  }

}
